'use client'

import { useCallback } from 'react'
import Sentry from '@/lib/sentry-safe'
import { TESLA_REQUIREMENTS } from '@/lib/audio-utils'
import { storageApi } from '@/lib/api/storage'
import { IOS_DOWNLOAD_EVENT } from '@/components/ui/ios-download-overlay'
import { triggerHaptic } from '@/lib/haptics'
import { play } from 'cuelume'
import { trackDownloadFailed } from '@/lib/tracking'

interface DownloadOptions {
  soundId?: string
  fileUrl?: string
  filename?: string
  blob?: Blob
  onBeforeDownload?: () => Promise<void>
  onSuccess?: () => void
  onError?: (error: Error) => void
  /** Funnel source for `download_failed`, reported only when the network fetch of `fileUrl` (or the iOS preflight) fails. */
  source?: string
}

function isIOSSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
  return isIOS && isSafari
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * iOS Safari saves through a top-level navigation to the download route, so a
 * failure there (rate limit, blocked or missing file, storage 404 behind the
 * redirect) would replace the page with the raw response. A HEAD request
 * follows the same route and redirect first; only a 2xx navigates.
 */
async function triggerServerDownload(soundId: string): Promise<void> {
  const url = `/api/sounds/${encodeURIComponent(soundId)}/download-file`
  const probe = await fetch(url, { method: 'HEAD', cache: 'no-store' })
  if (!probe.ok) {
    throw new Error(`Download preflight failed (HTTP ${probe.status})`)
  }
  window.location.href = url
}

export function useDownload() {
  const download = useCallback(async (options: DownloadOptions): Promise<{ success: boolean }> => {
    const {
      soundId,
      fileUrl,
      filename = TESLA_REQUIREMENTS.FILENAME,
      blob,
      onBeforeDownload,
      onSuccess,
      onError,
      source,
    } = options

    try {
      if (onBeforeDownload) {
        await onBeforeDownload()
      }

      if (blob) {
        triggerBlobDownload(blob, filename)
        triggerHaptic('download-success') // §12.8
        play('success')
        onSuccess?.()
        return { success: true }
      }

      if (isIOSSafari() && soundId && fileUrl) {
        await triggerServerDownload(soundId).catch((error: unknown) => {
          if (source) trackDownloadFailed({ sound_id: soundId, source, error })
          throw error
        })
        window.dispatchEvent(new CustomEvent(IOS_DOWNLOAD_EVENT))
        triggerHaptic('download-success') // §12.8
        play('success')
        onSuccess?.()
        return { success: true }
      }

      if (fileUrl) {
        // The only branch that actually waits on the network. `loading` marks
        // that user-initiated work has started, so the gap before `success`
        // reads as progress rather than a dead control. The blob branch above
        // is instant and the iOS preflight is one bodiless HEAD, so neither
        // gets it.
        play('loading')
        const file = await storageApi.downloadFile(fileUrl).catch((error: unknown) => {
          if (source) trackDownloadFailed({ sound_id: soundId, source, error })
          throw error
        })
        triggerBlobDownload(file, filename)
        triggerHaptic('download-success') // §12.8
        play('success')
        onSuccess?.()
        return { success: true }
      }

      throw new Error('Either blob or fileUrl must be provided')
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      // The single reporter for a failed download; callers only show it. A 429
      // is the visitor's own rate limit, an expected state, not a defect.
      if (!/\(HTTP 429\)/.test(err.message)) {
        Sentry.captureException(err, {
          tags: { context: 'useDownload', isIOSSafari: String(isIOSSafari()) },
        })
      }
      play('error')
      onError?.(err)
      return { success: false }
    }
  }, [])

  return { download, isIOSSafari: typeof window !== 'undefined' ? isIOSSafari() : false }
}
